/**
 * volumePane (HOF-310) — the tenant's OWN sold volume, as a column pane under
 * the Preisspiegel price plot.
 *
 * ── What this answers that a price line cannot ────────────────────────────────
 *
 * Kim's job at Wetterauer Früchtchen is the CHANNEL BALANCE — *"roughly 30 %
 * Großhandel / 40 % Einzelhandel / 30 % Direktvermarktung, so too much volume
 * never slides into one channel"* — and she asked for the volume share per
 * channel on 2026-08-13. A price line says what a kilo fetched in each channel;
 * it cannot say how much moved through each. The number is already in the
 * client: it is the denominator the mengengewichteter Preis is computed over,
 * computed by `aggregate` and then discarded.
 *
 * ── The one thing this module must not do ─────────────────────────────────────
 *
 * **It must not claim the column equals the price's denominator.** The issue's
 * own first acceptance criterion asserted both — Σ traded kg AND the weighted
 * denominator — and the two genuinely differ: `bucketPrice` drops an out-of-band
 * observation (HOF-19) BEFORE summing, while the kilos on that delivery were
 * physically delivered; and in the unweighted fallback branch there is no kg
 * denominator at all. The spec's AC 5 replaced the identity with a disclosure:
 * the volume counts every deal that states a quantity, and where a bucket has an
 * exclusion the hint says the volume includes deals the price does not. A silent
 * disagreement between the column and the line above it is the one defect on
 * this surface that would cost trust and produce no error anywhere.
 *
 * ── Why the pane is Verkauf-only, and why it is derived per LINE ──────────────
 *
 * The columns are the Verkauf deals' volume, so they are coupled to the Verkauf
 * lines: showing the volume of deals whose prices are hidden would be a
 * distinction without a subject. But a line is `(side × channel)` since #2486,
 * so in „Nach Kanal“ there is no single "Verkauf line" — there are up to four,
 * and each segment follows ITS OWN channel's line. The coupling is therefore
 * expressed as a set of visible dataKeys handed in by the view, never as a
 * predicate over a side.
 *
 * Everything here is pure. What the chart does with it is `PriceChart.tsx`.
 */
import { catalogUnitKg, groupKey, type AggregateModel, type BucketAgg, type GroupKey } from './aggregate';
import { CHANNEL_COLORS, channelSlotLabel, type AggChartRow, type ChartView } from './aggregateChartModel';
import type { TimeDomain } from '../../../components/charts/timeDomain';
import { toEpochDay } from './priceAt';

const DAY_MS = 86_400_000;

/** The `dataKey` namespace for a volume series — never a price key. */
export const VOLUME_KEY_PREFIX = 'vol__';

/** The recharts `dataKey` for the volume of one price line's group. */
export function volumeKey(group: GroupKey): string {
  return `${VOLUME_KEY_PREFIX}${group}`;
}

/**
 * The recharts `yAxisId` the pane rides.
 *
 * A SECOND axis and not a second scale on the price one: the two carry different
 * units (€/kg and kg) and a column measured against the price axis would be a
 * tonnage read off a euro scale. It is `hide`den — the reference charts (Binance,
 * Yahoo, Robinhood) draw no volume ticks either, and a second visible ladder
 * would cost the price axis the width it needs.
 */
export const VOLUME_AXIS_ID = 'volumen';

/** One stack: the channel segments of a bucket are one column, not four. */
export const VOLUME_STACK_ID = 'volumen';

/** What the chip and the „Alle zusammen“ series are called. */
export const VOLUME_LABEL = 'Volumen';

/**
 * The „Alle zusammen“ column's colour — slate-400.
 *
 * Deliberately NOT `SIDE_COLORS.verkauf`: the column is not the price line in
 * another shape, and painting it the line's teal invites the reader to read the
 * two as one series. Neutral is what the reference charts use, and it is the one
 * value on this plot that carries no channel and no side meaning. In „Nach
 * Kanal“ the segments take `CHANNEL_COLORS` instead, because there the hue IS
 * the attribution.
 */
export const VOLUME_COLUMN_COLOR = '#94a3b8';

/**
 * How much of the plot height the TALLEST column occupies.
 *
 * The spec's AC 2 asks for the bottom 20–25 %. It is implemented as an axis top
 * rather than as a layout: a hidden y-axis running `[0, max / fraction]` puts a
 * column of `max` at exactly this fraction of the plot, and every shorter column
 * in proportion — which is what keeps the price plot's own geometry untouched
 * (AC 2's second half) instead of squeezing it into 78 % of the height.
 */
export const VOLUME_PANE_FRACTION = 0.22;

/** The chip's own tooltip while the pane is available. */
export const VOLUME_CHIP_TITLE = 'Eigenes Verkaufsvolumen je Zeitraum ein- oder ausblenden';

/**
 * AC 9 — `mixed` suppresses the pane rather than picking a winner, in the same
 * fail-loud posture `resolvePublishedUnit` takes for the headline and the axis.
 * A column labelled in the wrong unit is #2944 one column over.
 */
export const VOLUME_MIXED_TITLE =
  'Volumen nicht verfügbar: die Beobachtungen dieser Kachel tragen mehr als eine Einheit, ' +
  'also gibt es keine Menge, mit der die Säulen ehrlich beschriftet werden könnten.';

/**
 * Codex round 2, P1 — the quantity identity is only sound in EUROS.
 *
 * `catalogUnitKg` is `amount / value`: money over a price. `provenance.amount` is
 * the line total in the source CURRENCY (euros), so on a `ct/l` tile — and
 * `ct/l` is a live catalog unit, the milk tiles use it — the quotient is
 * euros ÷ cents-per-litre, i.e. HECTOLITRES. €450 at 45 ct/l reads `10 l`
 * instead of 1.000 l: understated a hundredfold, and labelled `l` by
 * `quantityUnit('ct/l')`.
 *
 * The pane refuses rather than converting, in this module's own #2944 posture:
 * a confident wrong number is neither visible nor recoverable, and fixing the
 * scale would mean threading the published unit into `aggregate.catalogUnitKg`,
 * which is shared with the day panel and with `bucketPrice`. **The price line is
 * unaffected** — there the quantity is only a WEIGHT, so a uniform factor
 * cancels exactly — which is why nothing has ever surfaced this. The day panel's
 * own `kgSum` column has the same 100× error on those tiles today; that is a
 * pre-existing defect and is routed to `/triage` rather than fixed here.
 *
 * **HOF-340 fixed the day panel and this refusal STANDS, deliberately.** The
 * routed defect was taken as its own issue and closed at the display site:
 * `publishedUnit.quantityScale` / `formatTradedQuantity` apply the numerator
 * factor where the quotient leaves the weighting, so `catalogUnitKg` never had
 * to be threaded after all — the sentence above about what a fix would cost is
 * the reasoning of the day, kept because it is why the refusal was the right
 * call at the time, not because it is still the only option. Lifting the
 * refusal is now mechanically possible and is NOT this issue's business: the
 * pane's columns, its axis top, its bucket shares and its hint totals all carry
 * the same quantity, so it is a change to `buildVolumePane`'s model with its own
 * AC review — and shipping it inside a display-scale fix would have put an
 * unreviewed second surface on a unit no tenant has yet observed. Routed to
 * `/triage` from HOF-340.
 */
export const VOLUME_UNIT_SCALE_TITLE =
  'Volumen nicht verfügbar: die Preise dieser Kachel sind nicht in Euro angegeben, ' +
  'sodass sich aus Betrag und Preis keine belastbare Menge ableiten lässt.';

/**
 * Codex round 1 P2, reversed by codex round 2 P1 — an unresolved unit refuses.
 *
 * Round 1 flagged that a null unit prints `Volumen: 720` with no dimension, and
 * this file first kept that, on the consistency argument that `formatQuantity`
 * has printed a bare number since #2944 and the price y-axis renders unlabelled
 * in the same state. Round 2 supplied the reason that argument misses: an
 * unknown unit is not merely an unknown LABEL, it is an unknown SCALE — the tile
 * whose unit cannot be resolved may be the `ct/l` one, in which case the number
 * itself is wrong by a hundredfold and nothing on screen says so. A missing
 * label degrades gracefully; a wrong magnitude does not.
 */
export const VOLUME_UNKNOWN_UNIT_TITLE =
  'Volumen nicht verfügbar: zu dieser Kachel ist keine Einheit hinterlegt, ' +
  'sodass die Menge weder benannt noch verlässlich berechnet werden kann.';

/** The other reason a pane cannot be drawn: nothing states a quantity. */
export const VOLUME_NO_QUANTITY_TITLE =
  'Volumen nicht verfügbar: zu den eigenen Verkäufen in diesem Zeitraum ist keine Menge hinterlegt.';

/**
 * AC 5 — said on the hint of a bucket whose price dropped an observation.
 *
 * The sentence exists so the disagreement is STATED rather than discovered: a
 * reader who multiplies the column by the line and does not recover the bucket's
 * money is otherwise looking at two plausible numbers and no explanation.
 */
export const VOLUME_EXCLUDED_NOTE =
  'Enthält Geschäfte, die vom Preis ausgeschlossen sind (außerhalb der Bandbreite).';

/**
 * Codex round 1, P1 — said where the bucket holds deals that state NO quantity.
 *
 * They contribute nothing to the column (AC 4), which is right, and the tooltip
 * was then presenting the remainder as *the* bucket volume and dividing the
 * channel shares by it. That is the same silent-disagreement shape AC 5 exists
 * to prevent, one step out: on a farm where one channel's lines carry a line
 * total and another's do not, the advertised channel BALANCE — the whole point
 * of this pane — is biased by exactly the population nobody can measure. So the
 * number is disclosed as a lower bound rather than suppressed, which is the same
 * posture HOF-19 takes (excluded from the statistic, never hidden).
 */
export const VOLUME_MISSING_QUANTITY_NOTE =
  'Zu einzelnen Geschäften ist keine Menge hinterlegt: das Volumen ist eine Untergrenze, ' +
  'und die Anteile beziehen sich nur auf die erfasste Menge.';

/**
 * Codex round 1, P1 — said where the read behind the chart was incomplete.
 *
 * `dataComplete` already gates the off-season bridges (#2652) on exactly this
 * reasoning, and a Σ needs it MORE than a mean does: a mean over a subset is
 * still a plausible mean, while a total over a subset is simply too small, and a
 * truncation that fell disproportionately on one channel moves the balance the
 * reader is here to read. Disclosed rather than suppressed, because unlike a
 * bridge — a fabricated glyph — every column here is real observed volume.
 */
export const VOLUME_INCOMPLETE_READ_NOTE =
  'Der Lesevorgang war unvollständig — es können Geschäfte fehlen.';

/**
 * Why the pane cannot be drawn for this tile's UNIT, or `null` when it can.
 *
 * One function rather than three conditions at the call site, because all three
 * answers are the same kind of refusal and the chip prints exactly one of them.
 * See each constant for its own reasoning.
 */
export function volumeUnitRefusal(unit: string | null, mixed: boolean): string | null {
  if (mixed) return VOLUME_MIXED_TITLE;
  if (!unit) return VOLUME_UNKNOWN_UNIT_TITLE;
  // The identity is `amount / value` and `amount` is in euros, so the price has
  // to be too. `€/t`, `€/dt`, `€/kg`, `€/l`, `€/Stk`, `€/Einheit`, `€/ha`,
  // `€/h` and `€/m³` all pass; `ct/l` is the one live catalog unit that does not.
  if (!unit.startsWith('€/')) return VOLUME_UNIT_SCALE_TITLE;
  return null;
}

/** One stacked segment — the volume of exactly one price line's group. */
export interface VolumeSeries {
  /** recharts `dataKey`. */
  key: string;
  /** The line whose ACTUAL visibility this segment follows (AC 10/11). */
  lineId: GroupKey;
  label: string;
  color: string;
  /**
   * Whether this series has a measurable quantity ANYWHERE in the window — i.e.
   * whether it draws a column at all.
   *
   * An unmeasurable one is deliberately KEPT rather than dropped (codex round 1,
   * P1): it draws nothing, but it carries that group's quantity-less deals into
   * {@link volumeHint}'s `missingCount`. Dropped, a channel whose lines all lack
   * a line total would be invisible to the disclosure AND absent from the
   * shares, leaving the remaining channels reading 100 % of a balance nobody
   * measured — on the surface whose whole purpose is the channel balance.
   */
  measured: boolean;
}

/** One series' contribution to one bucket. */
export interface VolumeCell {
  /**
   * Σ `catalogUnitKg` over the bucket's observations, in the tile's own quantity
   * unit — or `null` where NOT ONE of them states a quantity.
   *
   * Null rather than 0, and the cell is kept rather than dropped: 0 draws a
   * column saying *nothing was sold here*, which is a different sentence from
   * *we cannot say how much was sold here* — and dropping the cell would take
   * {@link missingCount} with it, which is precisely the population the hint
   * has to disclose. A channel whose lines all lack a line total would then be
   * invisible to the hint AND absent from the shares, leaving the remaining
   * channels reading 100 % of a balance nobody measured.
   */
  qty: number | null;
  /**
   * Observations in the bucket that state no usable quantity, so contributed
   * NOTHING to {@link qty} — see {@link VOLUME_MISSING_QUANTITY_NOTE}.
   */
  missingCount: number;
  /**
   * Observations that are counted in {@link qty} AND were really dropped from
   * the bucket's price — AC 5's trigger, and **not** `BucketAgg.excludedCount`.
   *
   * Codex round 1, P2. Two states make that count the wrong one, and both are
   * reachable:
   *
   *  - **`allExcluded`.** When every observation in a bucket is marked,
   *    `bucketPrice` deliberately folds all of them back in — nothing was
   *    dropped, so "the volume includes deals the price excludes" is false.
   *    HOF-19 records this as the realistic case, not an edge one: the artefact
   *    that prompted it sat in a channel series with exactly one member.
   *  - **a marked observation with no usable quantity.** It contributes nothing
   *    to the column either, so again the sentence claims an inclusion that did
   *    not happen.
   *
   * A note that fires when the rule did not is the same defect as copy about a
   * glyph the reader cannot see — the #2777 estimate caption and HOF-19's own
   * footnote (AC 17) both learnt it.
   */
  excludedFromPrice: number;
}

export interface VolumeBucket {
  /** Bucket START, ISO — the same key the price rows and `?d=` use. */
  date: string;
  byKey: Record<string, VolumeCell>;
}

export interface VolumePaneModel {
  series: VolumeSeries[];
  /** Ascending by date, and only buckets that have at least one cell. */
  buckets: VolumeBucket[];
  byDate: Record<string, VolumeBucket>;
  /**
   * Whether the pane is the per-channel stack rather than one solid column —
   * i.e. whether the hint owes a per-segment breakdown (AC 8).
   */
  stacked: boolean;
}

/**
 * One bucket's own volume: Σ `catalogUnitKg(provenance, value)` over its
 * observations, or `null` where NONE of them states a quantity.
 *
 * Two asymmetries, both from AC 4 and both load-bearing:
 *
 *  - an observation the identity cannot answer for contributes **nothing** and
 *    does **not** null the bucket. An `amount`-less line is an ordinary state
 *    (the materialiser emits `amount: None`), and nulling the column would
 *    delete the volume of every deal that DID state one;
 *  - a bucket where nothing states a quantity is `null` rather than `0`. Zero
 *    draws a column saying *nothing was sold here*, which is a different
 *    sentence from *we cannot say how much was sold here*.
 *
 * It reads `sources` — every observation in the bucket, out-of-band ones
 * included — which is exactly where it parts company with `bucketPrice`. See
 * the module docstring.
 */
export function bucketVolume(bucket: BucketAgg): VolumeCell {
  let qty = 0;
  let any = false;
  let missingCount = 0;
  let excludedFromPrice = 0;
  for (const s of bucket.sources) {
    const own = catalogUnitKg(s.provenance, s.value);
    if (own == null) {
      missingCount += 1;
      continue;
    }
    any = true;
    qty += own;
    // BOTH halves required, and `own > 0` is the third: a 0 kg deal is "known
    // nothing", so counting it as volume the price excluded would claim an
    // inclusion the column cannot show. See {@link VolumeCell.excludedFromPrice}.
    if (own > 0 && s.outOfBand !== undefined && !bucket.allExcluded) excludedFromPrice += 1;
  }
  return { qty: any ? qty : null, missingCount, excludedFromPrice };
}

/** {@link bucketVolume}'s quantity alone, or null on a bucket that states none. */
export function bucketQuantity(bucket: BucketAgg): number | null {
  return bucketVolume(bucket).qty;
}

export interface BuildVolumePaneParams {
  /** The live year's own-price aggregate — never a prior-year one (AC 12). */
  model: AggregateModel;
  view: ChartView;
  /** The chart's fixed window, so the pane clips exactly as the lines do. */
  domain: TimeDomain;
  /** "Heute" as ISO — a live line stops there, and so does its volume. */
  today: string;
}

/**
 * The pane for one aggregate, or `null` when there is no own Verkauf volume to
 * draw at all.
 *
 * The group plan MIRRORS `aggregateChartModel.planGroups` for the Verkauf side,
 * deliberately and rule for rule — including its fallback to the combined
 * `(side, 'all')` group when the side carries no channels — because the segment
 * ids ARE the live line ids, and a plan that drifted would produce segments
 * coupled to lines that do not exist. That identity is also what keeps a
 * benchmark line out without a filter: a benchmark id is `bm__…`, which
 * `groupKey` can never mint.
 */
export function buildVolumePane(params: BuildVolumePaneParams): VolumePaneModel | null {
  const { model, view, domain, today } = params;
  if (!model.sidesPresent.includes('verkauf')) return null;

  const channels = view === 'kanal' ? (model.channelsBySide.verkauf ?? []) : [];
  const plans: VolumeSeries[] =
    channels.length === 0
      ? [
          {
            key: volumeKey(groupKey('verkauf', 'all')),
            lineId: groupKey('verkauf', 'all'),
            label: VOLUME_LABEL,
            color: VOLUME_COLUMN_COLOR,
            measured: false,
          },
        ]
      : channels.map((channel) => ({
          key: volumeKey(groupKey('verkauf', channel)),
          lineId: groupKey('verkauf', channel),
          label: channelSlotLabel(channel),
          color: CHANNEL_COLORS[channel],
          measured: false,
        }));

  // The live line's own clip (`buildAggregateChartModel`): inside the fixed
  // window and not after today. A bucket outside it has no row to sit on, so a
  // column there would either be dropped by recharts or drawn at a fabricated x.
  //
  // It is taken on the bucket's START DATE, exactly as the lines take it, and
  // that is what AC 6's invariance is bounded by: a 1M bucket starting three
  // days before the window opens is clipped whole, while the 1W buckets inside
  // it are kept, so Σ at the window EDGE differs between grains. The alternative
  // — clipping the pane on observation dates instead — would draw a column over
  // a stretch where the price line draws nothing, which is a worse disagreement
  // than an edge bucket. The fold itself is grain-invariant; the window is the
  // plot's, and it is shared.
  const loE = Math.round(domain.lo / DAY_MS);
  const hiE = Math.min(toEpochDay(today), Math.round(domain.hi / DAY_MS));

  const byDate = new Map<string, VolumeBucket>();
  const live = new Set<string>();
  for (const plan of plans) {
    for (const bucket of model.byGroup[plan.lineId] ?? []) {
      const e = toEpochDay(bucket.date);
      if (e < loE || e > hiE) continue;
      const cell = bucketVolume(bucket);
      const row = byDate.get(bucket.date) ?? { date: bucket.date, byKey: {} };
      row.byKey[plan.key] = cell;
      byDate.set(bucket.date, row);
      // Only a MEASURED cell makes its series live. One that holds nothing but a
      // `missingCount` still rides along, so the hint can disclose it, but it
      // never puts an all-null `<Bar>` on the plot.
      if (cell.qty != null) live.add(plan.key);
    }
  }
  if (live.size === 0) return null;

  // A series with no cell anywhere is dropped rather than drawn empty: it would
  // put a channel in the hint's breakdown that contributes nothing to any
  // column, which reads as "0 %" rather than as "not applicable".
  // Every plan that has a cell at all — measured or not; see `VolumeSeries.measured`.
  const seen = new Set<string>();
  for (const row of byDate.values()) for (const key of Object.keys(row.byKey)) seen.add(key);
  const series = plans
    .filter((p) => seen.has(p.key))
    .map((p) => ({ ...p, measured: live.has(p.key) }));
  const buckets = [...byDate.values()]
    // A bucket nothing could be measured in draws no column, so it carries no
    // hint either — and one kept here would put an empty row on the plot.
    .filter((b) => Object.values(b.byKey).some((cell) => cell.qty != null))
    .sort((a, b) => toEpochDay(a.date) - toEpochDay(b.date));
  return {
    series,
    buckets,
    byDate: Object.fromEntries(buckets.map((b) => [b.date, b])),
    stacked: series.filter((v) => v.measured).length > 1,
  };
}

/**
 * The hidden axis's top, given the tallest stacked column on the plot.
 *
 * Never 0: a `[0, 0]` domain is degenerate and recharts falls back to its own
 * nicing, which would silently restore a full-height pane.
 */
export function volumeAxisTop(max: number): number {
  return max > 0 ? max / VOLUME_PANE_FRACTION : 1;
}

/** The tallest stacked column over the VISIBLE series — the axis's input. */
export function volumeAxisMax(pane: VolumePaneModel, visibleKeys: readonly string[]): number {
  let max = 0;
  for (const bucket of pane.buckets) {
    let total = 0;
    for (const key of visibleKeys) total += bucket.byKey[key]?.qty ?? 0;
    if (total > max) max = total;
  }
  return max;
}

/**
 * The price rows with the VISIBLE volume series written onto them.
 *
 * A new array of new objects — the price model is not mutated by the pane being
 * switched on, which is what makes AC 2's "every price value is unchanged by the
 * pane's presence" structural rather than a promise. No row is ADDED: a volume
 * bucket is a price bucket, so its row already exists.
 *
 * A row the pane has nothing for gets an explicit `null` rather than no key at
 * all, so recharts reads a gap off the row instead of resolving the key up the
 * prototype chain.
 */
export function volumeRowsWith(
  rows: readonly AggChartRow[],
  pane: VolumePaneModel,
  visibleKeys: readonly string[],
): AggChartRow[] {
  if (visibleKeys.length === 0) return rows.map((row) => ({ ...row }));
  return rows.map((row) => {
    const next: AggChartRow = { ...row };
    const bucket = pane.byDate[row.date];
    for (const key of visibleKeys) next[key] = bucket?.byKey[key]?.qty ?? null;
    return next;
  });
}

/** One line of the hover hint. */
export interface VolumeHintRow {
  key: string;
  label: string;
  color: string;
  qty: number;
  /** Share of the VISIBLE total, in percent, or `null` on a zero total. */
  share: number | null;
}

export interface VolumeHint {
  date: string;
  rows: VolumeHintRow[];
  /**
   * Σ over the VISIBLE segments. A lower bound whenever {@link missingCount} is
   * non-zero, which is what {@link VOLUME_MISSING_QUANTITY_NOTE} says out loud.
   */
  total: number;
  /** Deals counted here that the price really dropped — AC 5's trigger. */
  excludedFromPrice: number;
  /** Deals in these buckets that state no quantity, so are in neither number. */
  missingCount: number;
}

/**
 * What the hover says at one bucket, over the series that are actually drawn.
 *
 * The shares are normalised over the VISIBLE total (AC 10): hiding the
 * Großhandel line must not leave Direktvermarktung reading 20 % of a total half
 * of which is no longer on the plot — a percentage of something the reader
 * cannot see is not a share.
 *
 * `null` when nothing visible has a column here, so the crosshair keeps saying
 * nothing on the buckets the pane has no answer for — including every row the
 * forecast and the window edge contribute (HOF-96 AC 17's posture, preserved).
 */
export function volumeHint(
  pane: VolumePaneModel,
  date: string,
  visibleKeys: readonly string[],
): VolumeHint | null {
  const bucket = pane.byDate[date];
  if (!bucket) return null;
  const visible = new Set(visibleKeys);
  const rows: VolumeHintRow[] = [];
  let total = 0;
  let excludedFromPrice = 0;
  let missingCount = 0;
  // Over EVERY visible key, and the two loops' scopes differ on purpose: a row is
  // drawn only for a MEASURED cell, while `missingCount` is collected from every
  // visible cell — including one that measured nothing at all. That second case
  // is the whole point of the disclosure (codex round 1, P1): a channel whose
  // lines carry no line total would otherwise leave the others reading 100 %.
  for (const s of pane.series) {
    if (!visible.has(s.key)) continue;
    const cell = bucket.byKey[s.key];
    if (!cell) continue;
    missingCount += cell.missingCount;
    if (cell.qty == null) continue;
    rows.push({ key: s.key, label: s.label, color: s.color, qty: cell.qty, share: null });
    total += cell.qty;
    excludedFromPrice += cell.excludedFromPrice;
  }
  if (rows.length === 0) return null;
  for (const row of rows) row.share = total > 0 ? (row.qty / total) * 100 : null;
  return { date, rows, total, excludedFromPrice, missingCount };
}

/** A share as the hint prints it — whole percent, German. */
export function formatShare(share: number): string {
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(share)} %`;
}

/**
 * The width of one column, in px.
 *
 * recharts derives a bar's width from the axis band, and this axis is NUMERIC
 * and one year wide with `allowDataOverflow` — so the band is whatever spacing
 * the rows happen to have, which at 1M is a month and at 1T a day. Left to
 * itself that draws month columns wide enough to hide the price line behind
 * them. So the width is stated: the bucket's share of the plot, at 70 % so the
 * columns read as a series of marks rather than as a filled area, capped.
 *
 * `plotWidthPx` is 0 until the plot has been laid out (and always 0 under
 * jsdom), which is why the fallback is a plausible desktop width rather than 0 —
 * an unmeasured plot must still draw a visible column.
 */
export const VOLUME_BAR_MAX_PX = 26;
export const VOLUME_BAR_MIN_PX = 1;
const FALLBACK_PLOT_PX = 720;
const BUCKETS_PER_YEAR: Record<string, number> = { day: 365, week: 52, month: 12 };

export function volumeBarSizePx(grain: string, plotWidthPx: number): number {
  const width = plotWidthPx > 0 ? plotWidthPx : FALLBACK_PLOT_PX;
  const buckets = BUCKETS_PER_YEAR[grain] ?? 52;
  return Math.max(VOLUME_BAR_MIN_PX, Math.min(VOLUME_BAR_MAX_PX, Math.floor((width / buckets) * 0.7)));
}
